/**
 * Free Gifts Cart Integration
 * Automatically adds free gifts to cart when bundle is selected
 */

class FreeGiftsCart {
  constructor() {
    this.variantChangeUnsubscriber = undefined;
    this.isListenerSetup = false;
    this.init();
  }

  init() {
    // Listen for variant changes using Dawn's pub/sub system
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    
    // Also listen for direct variant selector changes
    this.setupVariantSelectorListener();
    
    // Listen for form submission
    document.addEventListener('submit', this.handleFormSubmit.bind(this));
    
    // Initialize on page load
    this.updateFreeGiftsDisplay();
  }

  setupVariantSelectorListener() {
    // Prevent multiple listeners
    if (this.isListenerSetup) return;
    
    // Listen for bundle card selections using event delegation to avoid duplicates
    document.addEventListener('change', (event) => {
      if (event.target.matches('.Bundle_card__input')) {
        // Add small delay to ensure DOM is updated
        setTimeout(() => {
          this.updateFreeGiftsDisplay();
        }, 50);
      }
    });
    
    this.isListenerSetup = true;
  }

  getVariantById(variantId) {
    // Get variant data from the page
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;
    
    const selectedVariantData = variantSelects.querySelector('[data-selected-variant]');
    if (!selectedVariantData) return null;
    
    try {
      const variantData = JSON.parse(selectedVariantData.textContent);
      
      // If the current variant matches the requested ID, return it
      if (variantData.id.toString() === variantId.toString()) {
        return variantData;
      }
      
      // Otherwise, try to get all variants from the product
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const variantOptions = productForm.querySelectorAll('option[value]');
        for (let option of variantOptions) {
          if (option.value === variantId) {
            // Create a variant object from the option data
            return {
              id: parseInt(variantId),
              title: option.textContent.trim(),
              available: !option.disabled
            };
          }
        }
      }
      
      // Fallback: return the current variant
      return variantData;
    } catch (e) {
      return null;
    }
  }

  handleVariantChange(event) {
    // Don't use the variant from the event, just update based on selected bundle card
    this.updateFreeGiftsDisplay();
  }

  handleFormSubmit(event) {
    // Only handle product form submissions (check multiple possible form selectors)
    const isProductForm = event.target.classList.contains('product-form') || 
                         event.target.querySelector('.product-form__buttons') ||
                         event.target.id.includes('product-form') ||
                         event.target.closest('.product');
    
    if (!isProductForm) return;
    
    
    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) {
      return;
    }
    
    
    // Get free gifts for this variant
    const freeGifts = this.getFreeGiftsForVariant(selectedVariant);
    
    if (freeGifts.length > 0) {
      // Prevent default form submission
      event.preventDefault();
      
      // First submit the main product, then add free gifts
      this.submitMainProductThenFreeGifts(event.target, freeGifts);
    }
  }

  getSelectedVariant() {
    // Method 1: Try variant-selects (standard Dawn)
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedVariantData = variantSelects.querySelector('[data-selected-variant]');
      if (selectedVariantData) {
        try {
          return JSON.parse(selectedVariantData.textContent);
        } catch (e) {
          // Silent error handling
        }
      }
    }
    
    // Method 2: Try bundle cards (custom implementation)
    const selectedBundleCard = document.querySelector('.Bundle_card__input:checked');
    if (selectedBundleCard) {
      const variantTitle = selectedBundleCard.value;
      // Get variant from global product data
      if (window.productVariants) {
        const variant = window.productVariants.find(v => v.title === variantTitle);
        if (variant) return variant;
      }
      
      // Fallback: create variant object from bundle card data
      return {
        title: variantTitle,
        price: selectedBundleCard.dataset.price || 0,
        id: selectedBundleCard.dataset.variantId || null
      };
    }
    
    // Method 3: Try product form variant input
    const variantInput = document.querySelector('[name="id"]:checked, select[name="id"]');
    if (variantInput) {
      const variantId = variantInput.value;
      if (window.productVariants) {
        const variant = window.productVariants.find(v => v.id == variantId);
        if (variant) return variant;
      }
    }
    
    return null;
  }

  getFreeGiftsForVariant(variant) {
    const freeGifts = [];
    
    if (!variant || !variant.title) return freeGifts;
    
    
    // Use the same logic as updateFreeGiftsDisplay to get variant position
    let variantPosition = 1; // Default fallback
    
    // Method 1: Try to get from selected bundle card
    const selectedBundleCard = document.querySelector('.Bundle_card__input:checked');
    if (selectedBundleCard) {
      const bundleVariantTitle = selectedBundleCard.value;
      const allBundleCards = document.querySelectorAll('.Bundle_card__input');
      
      allBundleCards.forEach((card, index) => {
        if (card.value === bundleVariantTitle) {
          variantPosition = index + 1;
        }
      });
    } else {
      // Method 2: Try to extract from variant title if it contains numbers
      const titleMatch = variant.title.match(/(\d+)/);
      if (titleMatch) {
        variantPosition = parseInt(titleMatch[1]);
      } else {
        // Method 3: Use variant index in product variants list
        if (window.productVariants) {
          const variantIndex = window.productVariants.findIndex(v => v.id === variant.id);
          if (variantIndex !== -1) {
            variantPosition = variantIndex + 1;
          }
        }
      }
    }
    
    
    // Get free gifts from the page data attributes
    const freeGiftsSection = document.querySelector('.free-gifts-section');
    if (!freeGiftsSection) {
      return freeGifts;
    }
    
    // Get gift cards and their data
    const giftCards = freeGiftsSection.querySelectorAll('.free-gift-card');
    
    giftCards.forEach(card => {
      const giftId = card.getAttribute('data-gift-id');
      const giftProductId = card.getAttribute('data-gift-product-id');
      const giftTitle = card.getAttribute('data-gift-title');
      const giftPrice = card.getAttribute('data-gift-price');
      
      
      if (!giftProductId || giftProductId === 'null') {
        return;
      }
      
      // Determine if this gift should be unlocked based on variant position
      // Bundle 1 (position 1) = no gifts
      // Bundle 2 (position 2) = gifts 1 & 2
      // Bundle 3 (position 3) = gifts 1, 2 & 3
      let shouldUnlock = false;
      
      if (giftId === '1') {
        shouldUnlock = variantPosition >= 2;
      } else if (giftId === '2') {
        shouldUnlock = variantPosition >= 2;
      } else if (giftId === '3') {
        shouldUnlock = variantPosition >= 3;
      }
      
      
      if (shouldUnlock) {
        // For cart.add, we need variant ID, not product ID
        // Try to get the first available variant ID for the gift product
        let variantId = giftProductId; // Fallback to product ID if no variant found
        
        // Check if we can find variant data
        if (window.productVariants && window.currentProductData) {
          // Look for gift product in global data or try to get first variant
          // For now, assume giftProductId is already a variant ID or we'll fix this in Liquid
        }
        
        freeGifts.push({
          id: variantId,
          title: giftTitle,
          price: parseFloat(giftPrice) || 0,
          type: 'product' // Tous les gifts sont des produits, pas de shipping
        });
      }
    });
    

    return freeGifts;
  }

  getFreeGiftsConfig() {
    // This method is no longer used, but keeping for compatibility
    return null;
  }

  async submitMainProductThenFreeGifts(form, freeGifts) {
    try {
      // Get main product data from form
      const formData = new FormData(form);
      const mainProductId = formData.get('id');
      const mainProductQuantity = formData.get('quantity') || '1';
      
      // Get main product properties
      const mainProductProperties = {};
      for (let [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          mainProductProperties[key] = value;
        }
      }
      
      // Prepare unified items array
      const items = [
        {
          id: parseInt(mainProductId),
          quantity: parseInt(mainProductQuantity),
          properties: mainProductProperties
        }
      ];
      
      // Add free gifts to the same request (multiply by main product quantity)
      const mainQuantity = parseInt(mainProductQuantity);
      freeGifts.forEach(gift => {
        items.push({
          id: parseInt(gift.id),
          quantity: mainQuantity,
          properties: {
            '_free_gift': 'true'
          }
        });
      });
      
      // Submit everything in one unified request
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add products to cart');
      }
      
      const result = await response.json();
      
      // Trigger cart update events immediately
      this.triggerCartUpdateEvents();
      
    } catch (error) {
      // Fallback: submit form normally
      form.submit();
    }
  }
  
  async addFreeGiftsToCart(freeGifts) {
    if (!freeGifts || freeGifts.length === 0) {

      return;
    }
    

    
    try {
      // 1. Récupérer l'état actuel du panier

      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      
      // 2. Calculer les quantités finales

      const updates = {};
      
      // Conserver toutes les quantités actuelles
      currentCart.items.forEach(item => {
        updates[item.variant_id] = item.quantity;
      });
      
      // Ajouter les free gifts
      freeGifts.forEach(gift => {
        const variantId = gift.id;
        const currentQty = updates[variantId] || 0;
        updates[variantId] = currentQty + 1;

      });
      

      
      // 3. Ajouter d'abord les free gifts individuellement pour les propriétés
      // Car Cart Update API ne gère pas les propriétés directement
      for (const gift of freeGifts) {
        const existingItem = currentCart.items.find(item => item.variant_id == gift.id);
        
        // Si le gift n'existe pas déjà, l'ajouter avec les propriétés
        if (!existingItem) {

          
          const giftFormData = new FormData();
          giftFormData.append('id', gift.id);
          giftFormData.append('quantity', '1');
          giftFormData.append('properties[_free_gift]', 'true');
          
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            body: giftFormData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (addResponse.ok) {

          } else {

          }
          
          // Petit délai pour éviter les conflits
          await new Promise(resolve => setTimeout(resolve, 50));
        } else {
          // Si existe déjà, juste incrémenter via update

        }
      }
      
      // 4. Mettre à jour toutes les quantités en un seul appel

      
      const updateFormData = new FormData();
      Object.entries(updates).forEach(([variantId, quantity]) => {
        updateFormData.append(`updates[${variantId}]`, quantity.toString());
      });
      
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        body: updateFormData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (updateResponse.ok) {
        const result = await updateResponse.json();
      } else {
        const errorText = await updateResponse.text();

        
        // Fallback: utiliser l'ancienne méthode séquentielle

        return this.addFreeGiftsSequentially(freeGifts);
      }
      
    } catch (error) {
      // Fallback: utiliser l'ancienne méthode séquentielle
      return this.addFreeGiftsSequentially(freeGifts);
    }
    

  }
  
  async addFreeGiftsSequentially(freeGifts) {

    
    for (const gift of freeGifts) {
      try {

        
        const giftFormData = new FormData();
        giftFormData.append('id', gift.id);
        giftFormData.append('quantity', '1');
        giftFormData.append('properties[_free_gift]', 'true');
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: giftFormData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.ok) {
          const result = await response.json();

        } else {
          const errorText = await response.text();

        }
        
        await new Promise(resolve => setTimeout(resolve, 50));
        
      } catch (error) {
        // Silent error handling
      }
    }
  }

  async waitForShopifyCartProcessing() {
    // Méthode intelligente pour attendre que Shopify termine
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20; // Maximum 2 secondes
      
      const checkShopifyReady = () => {
        attempts++;
        
        // Vérifier si les processus Shopify sont terminés
        const submitButton = document.querySelector('.product-form__submit');
        const isLoading = submitButton?.classList.contains('loading');
        const cartDrawer = document.querySelector('cart-drawer');
        
        if (!isLoading || attempts >= maxAttempts) {

          resolve();
        } else {
          setTimeout(checkShopifyReady, 50);
        }
      };
      
      // Délai initial minimal puis vérification intelligente
      setTimeout(checkShopifyReady, 150);
    });
  }

  
  async triggerCartUpdateEvents() {

    
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {

      return;
    }
    
    try {
      // Récupérer les données du panier rapidement
      const cartJsonResponse = await fetch('/cart.js');
      const cartData = await cartJsonResponse.json();

      
      // Publier l'événement Dawn (plus rapide que les sections HTML)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {

        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'free-gifts-cart',
          cartData: cartData
        });
      }
      
      // Ouvrir le drawer immédiatement
      if (cartDrawer.open && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('animate', 'active');
        cartDrawer.setAttribute('aria-hidden', 'false');
      }
      

      
    } catch (error) {
      // Fallback rapide
      const cartIcon = document.querySelector('[data-cart-drawer-target], .cart-icon, [href="/cart"]');
      if (cartIcon && cartIcon.click) {
        cartIcon.click();
      }
    }
  }

  addFreeGiftsToForm(form, freeGifts) {
    // Legacy method - now only used as fallback
    const existingInputs = form.querySelectorAll('input[name^="gift_"]');
    existingInputs.forEach(input => input.remove());
    
    freeGifts.forEach((gift, index) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `gift_${gift.id}`;
      input.value = gift.id;
      input.setAttribute('data-gift-title', gift.title);
      input.setAttribute('data-gift-price', gift.price);
      input.setAttribute('data-gift-type', gift.type || 'product');
      
      form.appendChild(input);
    });
  }

  updateFreeGiftsDisplay(variant = null) {
    // Update the free gifts section display
    const freeGiftsSection = document.querySelector('.free-gifts-section');
    if (!freeGiftsSection) {

      return;
    }
    
    // Get variant position/order instead of quantity
    let variantPosition = 0;
    
    // Method 1: Try to get from selected bundle card position
    const selectedBundleCard = document.querySelector('.Bundle_card--selected');
    if (selectedBundleCard) {
      const bundleCards = document.querySelectorAll('.Bundle_card');
      bundleCards.forEach((card, index) => {
        if (card === selectedBundleCard) {
          variantPosition = index + 1; // +1 because index starts at 0

        }
      });
    }
    
    // Method 2: If no selected bundle card, try to find the checked bundle card
    if (variantPosition === 0) {
      const bundleCards = document.querySelectorAll('.Bundle_card');
      bundleCards.forEach((card, index) => {
        const input = card.querySelector('.Bundle_card__input');
        if (input && input.checked) {
          variantPosition = index + 1; // +1 because index starts at 0

        }
      });
    }
    
    // Method 3: If still no position, try to get from variant data (fallback)
    if (variantPosition === 0) {
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        const selectedVariantData = variantSelects.querySelector('[data-selected-variant]');
        if (selectedVariantData) {
          try {
            const variantData = JSON.parse(selectedVariantData.textContent);
            // Try to get position from variant ID or other identifier

          } catch (e) {

          }
        }
      }
    }
    

    
    // Update each gift card based on variant position
    const giftCards = freeGiftsSection.querySelectorAll('.free-gift-card');
    giftCards.forEach(card => {
      const giftId = card.getAttribute('data-gift-id');
      const giftTitle = card.getAttribute('data-gift-title');
      
      // Determine if this gift should be unlocked based on variant position
      let shouldUnlock = false;
      
      if (giftId === '1') {
        shouldUnlock = variantPosition >= 2; // Second variant unlocks gift 1
      } else if (giftId === '2') {
        shouldUnlock = variantPosition >= 2; // Second variant unlocks gift 2
      } else if (giftId === '3') {
        shouldUnlock = variantPosition >= 3; // Third variant unlocks gift 3
      }
      
      
      // Update card classes and visibility
      if (shouldUnlock) {
        card.classList.remove('free-gift-card--locked');
        card.classList.add('free-gift-card--unlocked');
        
        // Show image, hide lock
        const image = card.querySelector('.free-gift-card__image');
        const lock = card.querySelector('.free-gift-card__lock');
        
        if (image) {
          image.style.display = 'flex';
          image.style.visibility = 'visible';
        }
        if (lock) {
          lock.style.display = 'none';
          lock.style.visibility = 'hidden';
        }
        
      } else {
        card.classList.remove('free-gift-card--unlocked');
        card.classList.add('free-gift-card--locked');
        
        // Show lock, hide image
        const image = card.querySelector('.free-gift-card__image');
        const lock = card.querySelector('.free-gift-card__lock');
        
        if (image) {
          image.style.display = 'none';
          image.style.visibility = 'hidden';
        }
        if (lock) {
          lock.style.display = 'flex';
          lock.style.visibility = 'visible';
        }
      }
    });
  }

  // Cleanup method
  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  // Utility method to format money
  formatMoney(amount) {
    if (typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
      return window.theme.moneyFormat.replace('{{amount}}', (amount / 100).toFixed(2));
    }
    return `$${(amount / 100).toFixed(2)}`;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.freeGiftsCartInstance = new FreeGiftsCart();
});

// Export for use in other scripts
window.FreeGiftsCart = FreeGiftsCart;